import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Ruler, ArrowUpRight } from "lucide-react";

// Caliper per SHEET (leaf) in inches — industry-typical values for US text stocks.
const STOCKS = [
  { key: "50u", label: "50# Uncoated Text (75 gsm) — 512 PPI", caliper: 0.0039 },
  { key: "60u", label: "60# Uncoated Text (90 gsm) — 444 PPI", caliper: 0.0045 },
  { key: "70u", label: "70# Uncoated Text (105 gsm) — 364 PPI", caliper: 0.0055 },
  { key: "80u", label: "80# Uncoated Text (120 gsm) — 320 PPI", caliper: 0.00625 },
  { key: "80g", label: "80# Gloss Text (120 gsm) — 588 PPI", caliper: 0.0034 },
  { key: "100g", label: "100# Gloss Text (150 gsm) — 444 PPI", caliper: 0.0045 },
  { key: "80m", label: "80# Matte / Silk Text (120 gsm) — 465 PPI", caliper: 0.0043 },
  { key: "100m", label: "100# Matte / Silk Text (150 gsm) — 364 PPI", caliper: 0.0055 },
  { key: "cream", label: "55# Cream Book / Novel stock — 410 PPI", caliper: 0.00488 },
];

const BINDINGS = [
  { key: "case", label: "Hardcover — Case bound (rounded or square back)", boards: 2, boardIn: 0.098, hinge: 0.0625 },
  { key: "case3", label: "Hardcover — Case bound, heavy 3 mm boards", boards: 2, boardIn: 0.118, hinge: 0.0625 },
  { key: "perfect", label: "Softcover — Perfect bound / PUR", boards: 0, boardIn: 0, hinge: 0, cover: 0.012 },
];

const fmtIn = (v) => `${v.toFixed(3)}"`;
const fmtMm = (v) => `${(v * 25.4).toFixed(1)} mm`;
const nearest32 = (v) => `${Math.round(v * 32)}/32"`;

export function SpineCalculator() {
  const [pages, setPages] = useState(240);
  const [stock, setStock] = useState("60u");
  const [binding, setBinding] = useState("case");

  const r = useMemo(() => {
    const s = STOCKS.find((x) => x.key === stock);
    const b = BINDINGS.find((x) => x.key === binding);
    const p = Math.max(2, Math.min(2000, Number(pages) || 0));
    const leaves = Math.ceil(p / 2);
    const block = leaves * s.caliper;
    const spine = block + b.boards * b.boardIn + (b.cover || 0) * 2;
    const wrap = b.key === "perfect" ? spine : spine + b.hinge; // cover artwork spine incl. hinge allowance
    return { p, leaves, block, spine, wrap, s, b };
  }, [pages, stock, binding]);

  const tooThin = r.b.key !== "perfect" && r.block < 0.1875;

  return (
    <div className="not-prose my-8 border-2 border-slate-900 bg-white" data-testid="spine-calculator">
      <div className="bg-slate-900 text-white px-5 py-3 flex items-center gap-2 font-mono text-[11px] tracking-[0.22em] uppercase font-bold">
        <Ruler className="w-4 h-4 text-kolt-orange" /> Book spine calculator
      </div>
      <div className="grid md:grid-cols-3 gap-4 p-5">
        <label className="block text-sm">
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500 block mb-1">Page count (pages, not sheets)</span>
          <input type="number" min={2} max={2000} step={2} value={pages} onChange={(e) => setPages(e.target.value)}
            className="w-full border border-slate-300 px-3 py-2 font-display font-bold text-lg" data-testid="spine-pages" />
        </label>
        <label className="block text-sm md:col-span-2">
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500 block mb-1">Interior paper</span>
          <select value={stock} onChange={(e) => setStock(e.target.value)} className="w-full border border-slate-300 px-3 py-2.5 text-sm bg-white" data-testid="spine-stock">
            {STOCKS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </label>
        <label className="block text-sm md:col-span-3">
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500 block mb-1">Binding</span>
          <select value={binding} onChange={(e) => setBinding(e.target.value)} className="w-full border border-slate-300 px-3 py-2.5 text-sm bg-white" data-testid="spine-binding">
            {BINDINGS.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
          </select>
        </label>
      </div>
      <div className="grid sm:grid-cols-3 gap-px bg-slate-200 border-t border-slate-200">
        {[
          ["Text block", r.block, "leaves × caliper"],
          ["Finished spine", r.spine, r.b.boards ? "block + 2 boards" : "block + covers"],
          ["Cover-art spine width", r.wrap, r.b.boards ? "incl. hinge allowance" : "set your PDF spine to this"],
        ].map(([label, v, sub], i) => (
          <div key={label} className={`bg-white p-4 ${i === 2 ? "bg-kolt-paper-soft" : ""}`}>
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500">{label}</div>
            <div className="font-display font-black text-2xl text-kolt-slate-dark leading-tight" data-testid={`spine-result-${i}`}>{fmtIn(v)}</div>
            <div className="text-xs text-slate-500">{fmtMm(v)} · ≈ {nearest32(v)} · {sub}</div>
          </div>
        ))}
      </div>
      <div className="px-5 py-3 text-xs text-slate-600 border-t border-slate-200 space-y-1">
        <div>{r.p} pages = {r.leaves} leaves × {r.s.caliper.toFixed(4)}" caliper. {r.b.boards ? `Boards: 2 × ${r.b.boardIn}"` : "Cover: 2 × 0.012\""}. Results are a pre-press estimate; final spine is confirmed on your proof.</div>
        {tooThin && <div className="text-amber-700 font-semibold" data-testid="spine-warning">Under 3/16" of text block — spine text will not fit and casing is not recommended. Add pages, choose a bulkier stock, or go perfect-bound.</div>}
      </div>
      <div className="px-5 pb-5 flex flex-wrap gap-3">
        <Link to="/shop/product/booklet-hard-cover-case" className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="spine-cta-hardcover">Price a hardcover book <ArrowUpRight className="w-4 h-4" /></Link>
        <Link to="/quote" className="btn-outline inline-flex items-center gap-2 text-sm" data-testid="spine-cta-quote">Send us your PDF for a checked spine</Link>
      </div>
    </div>
  );
}
